/**
 * useStripePayment Hook
 *
 * Manages Stripe initialization, PaymentIntent creation,
 * existing payment checks, and payment state recovery.
 *
 * Extracted from CardDetails wrapper component.
 *
 * Responsibilities:
 * - Load Stripe instance (from API config or fallback key)
 * - Check for existing / recovered payments
 * - Create PaymentIntent and expose clientSecret
 * - LocalStorage-based payment state for crash recovery
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import {
  createPaymentIntent,
  getStripeConfig,
  confirmPayment as confirmPaymentWithBackend,
  checkExistingPayment,
  CreditPackageId,
  mapPlanToPackageId,
} from '../services/stripePaymentService';

// Singleton Stripe instance
let stripeInstance: Stripe | null = null;

// Fallback publishable key
const STRIPE_PUBLISHABLE_KEY = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '';

// Payment state storage key
const PAYMENT_STATE_KEY = 'c1_payment_state';

export interface PaymentState {
  email: string;
  packageId: string;
  paymentIntentId?: string;
  status: 'pending' | 'processing' | 'succeeded' | 'failed';
  timestamp: number;
}

export function getStoredPaymentState(): PaymentState | null {
  try {
    const stored = localStorage.getItem(PAYMENT_STATE_KEY);
    if (!stored) return null;
    const state = JSON.parse(stored) as PaymentState;
    if (Date.now() - state.timestamp > 60 * 60 * 1000) {
      localStorage.removeItem(PAYMENT_STATE_KEY);
      return null;
    }
    return state;
  } catch {
    return null;
  }
}

export function storePaymentState(state: PaymentState): void {
  try {
    localStorage.setItem(PAYMENT_STATE_KEY, JSON.stringify(state));
  } catch (_) {
    // ignore
  }
}

export function clearPaymentState(): void {
  try {
    localStorage.removeItem(PAYMENT_STATE_KEY);
  } catch (_) {
    // ignore
  }
}

async function getStripePromise(): Promise<Stripe | null> {
  if (stripeInstance) return stripeInstance;
  try {
    const config = await getStripeConfig();
    const key = config.publishableKey || STRIPE_PUBLISHABLE_KEY;
    if (!key || !key.startsWith('pk_')) throw new Error('Invalid publishable key');
    stripeInstance = await loadStripe(key, { developerTools: { assistant: { enabled: false } } });
    return stripeInstance;
  } catch {
    stripeInstance = await loadStripe(STRIPE_PUBLISHABLE_KEY, { developerTools: { assistant: { enabled: false } } });
    return stripeInstance;
  }
}

export type PaymentInitStatus = 'loading' | 'ready' | 'already-paid' | 'error';

export interface UseStripePaymentOptions {
  email: string;
  customerName: string;
  planType: string;
  partyAbn?: string;
  partyType?: string;
  /** Override payment method types sent to backend, e.g. ['au_becs_debit'] */
  paymentMethodTypes?: string[];
  /** Callback when payment is already done (auto-skip) */
  onAlreadyPaid?: (data: { email: string; paymentMethodId: string; credits: number }) => void;
}

export interface UseStripePaymentReturn {
  /** Status of payment initialization */
  status: PaymentInitStatus;
  /** Error message (if status === 'error') */
  error: string;
  /** Stripe promise for <Elements> */
  stripePromise: Promise<Stripe | null> | null;
  /** Client secret for PaymentElement */
  clientSecret: string | null;
  /** Payment intent ID for confirmation */
  paymentIntentId: string | null;
  /** Package ID sent to backend */
  packageId: CreditPackageId;
  /** Confirm payment with backend after Stripe succeeds */
  confirmWithBackend: (paymentIntentIdOverride: string) => Promise<{ success: boolean; credits: number }>;
}

export function useStripePayment(options: UseStripePaymentOptions): UseStripePaymentReturn {
  const { email, customerName, planType, partyAbn, partyType, paymentMethodTypes, onAlreadyPaid } = options;
  const packageId: CreditPackageId = mapPlanToPackageId(planType || 'STARTER');

  const [status, setStatus] = useState<PaymentInitStatus>('loading');
  const [error, setError] = useState('');
  const [stripePromiseState, setStripePromiseState] = useState<Promise<Stripe | null> | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [paymentIntentId, setPaymentIntentId] = useState<string | null>(null);
  const initRef = useRef(false);

  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;

    const init = async () => {
      try {
        // Load Stripe
        const stripe = await getStripePromise();
        setStripePromiseState(Promise.resolve(stripe));

        // Check if this is a redirect return (from Klarna, etc.)
        const urlParams = new URLSearchParams(window.location.search);
        const redirectClientSecret = urlParams.get('payment_intent_client_secret');
        const isPaymentRedirect = urlParams.get('payment_redirect') === 'true' || 
                                   urlParams.has('redirect_status');

        if (isPaymentRedirect && redirectClientSecret) {
          // Use the existing client secret from the redirect URL
          // Extract payment intent ID from client secret (format: pi_xxx_secret_yyy)
          const piId = redirectClientSecret.split('_secret_')[0];
          setClientSecret(redirectClientSecret);
          setPaymentIntentId(piId);
          setStatus('ready');
          return;
        }

        // Check if already paid
        const serverCheck = await checkExistingPayment(email, packageId);
        if (serverCheck.hasPaid) {
          clearPaymentState();
          onAlreadyPaid?.({ email, paymentMethodId: 'already_paid', credits: 0 });
          setStatus('already-paid');
          return;
        }

        // Check localStorage for recoverable payment
        const storedState = getStoredPaymentState();
        if (
          storedState &&
          storedState.email === email &&
          storedState.packageId === packageId &&
          storedState.paymentIntentId
        ) {
          if (storedState.status === 'processing' || storedState.status === 'pending') {
            try {
              const confirmation = await confirmPaymentWithBackend(storedState.paymentIntentId);
              if (confirmation.success) {
                clearPaymentState();
                onAlreadyPaid?.({ email, paymentMethodId: 'recovered', credits: confirmation.credits });
                setStatus('already-paid');
                return;
              }
            } catch {}
          }
        }

        // Create PaymentIntent
        const response = await createPaymentIntent({
          packageId,
          customerEmail: email,
          customerName: customerName || 'Customer',
          idempotencyKey: `${email}_${packageId}_${Math.floor(Date.now() / 300000)}`,
          metadata: {
            partyAbn: partyAbn || '',
            partyType: partyType || '',
            planType: planType || '',
          },
          ...(paymentMethodTypes?.length ? { paymentMethodTypes } : {}),
        });

        if (response.alreadyPaid) {
          clearPaymentState();
          onAlreadyPaid?.({ email, paymentMethodId: 'already_paid', credits: 0 });
          setStatus('already-paid');
          return;
        }

        if (!response.clientSecret) throw new Error('No client secret received from server');

        setClientSecret(response.clientSecret);
        setPaymentIntentId(response.paymentIntentId);
        storePaymentState({ email, packageId, paymentIntentId: response.paymentIntentId, status: 'pending', timestamp: Date.now() });
        setStatus('ready');
      } catch (err: any) {
        setError(err.message || 'Failed to load payment form');
        setStatus('error');
      }
    };

    init();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const confirmWithBackend = useCallback(
    async (piId: string) => {
      const confirmation = await confirmPaymentWithBackend(piId);
      return { success: confirmation.success !== false, credits: confirmation.credits };
    },
    []
  );

  return {
    status,
    error,
    stripePromise: stripePromiseState,
    clientSecret,
    paymentIntentId,
    packageId,
    confirmWithBackend,
  };
}
